import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";

export type BatchStatus = "idle" | "running" | "done" | "error" | "canceled";

export interface BatchDocResult {
  ticketId: number;
  ok: boolean;
  message?: string;
  pdf?: string;
}

export interface BatchState {
  isOpen: boolean;
  title?: string;
  status: BatchStatus;
  total: number;
  processed: number;
  errorCount: number;
  currentIndex: number;
  results: (BatchDocResult | null)[];
}

const initialState: BatchState = {
  isOpen: false,
  title: undefined,
  status: "idle",
  total: 0,
  processed: 0,
  errorCount: 0,
  currentIndex: 0,
  results: [],
};

const batchSlice = createSlice({
  name: "batch",
  initialState,
  reducers: {
    startBatch: (state, { payload }: PayloadAction<{ total: number; title?: string }>) => {
      state.isOpen = true;
      state.title = payload.title ?? "Procesando documentos...";
      state.status = "running";
      state.total = payload.total;
      state.processed = 0;
      state.errorCount = 0;
      state.currentIndex = 0;
      state.results = Array(payload.total).fill(null);
    },
    tickBatch: (state, { payload }: PayloadAction<Partial<BatchDocResult>>) => {
      const idx = state.currentIndex;
      if (idx < state.results.length) {
        state.results[idx] = {
          ok: true,
          ticketId: payload.ticketId ?? 0,
          message: payload.message,
          pdf: payload.pdf,
        };
      }
      state.processed += 1;
      state.currentIndex += 1;
    },
    failBatch: (state, { payload }: PayloadAction<Partial<BatchDocResult>>) => {
      const idx = state.currentIndex;
      if (idx < state.results.length) {
        state.results[idx] = {
          ok: false,
          ticketId: payload.ticketId ?? 0,
          message: payload.message,
          pdf: payload.pdf,
        };
      }
      state.errorCount += 1;
      state.currentIndex += 1;
    },
    finishBatch: (state) => {
      state.status = "done";
      state.isOpen = false;
    },
    cancelBatch: (state) => { 
      state.status = "canceled";
      state.isOpen = false;
    },
    resetBatch: () => initialState,
    showBatch: (state) => {
      state.isOpen = true;
    },
  },
});

export const {
  startBatch,
  tickBatch,
  failBatch,
  finishBatch,
  cancelBatch,
  resetBatch,
  showBatch,
} = batchSlice.actions;

export const selectBatch = (state: RootState) => state.batchSlice;

export default batchSlice.reducer;
